import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../../shared/components/ui';
import { Calendar, CheckCircle, Info } from 'lucide-react';
import { DataCoverageInfo } from './DataCoverageInfo';
import { PatternAnalysis } from './PatternAnalysis';
import { AlignmentToggle } from './AlignmentToggle';
import { AlignmentComparisonTable } from './AlignmentComparisonTable';
import { usePatternMetrics } from '../../../../hooks/usePatternMetrics';

interface PriceData {
  data: Array<{ timestamp: string; priceUsd: number }>;
}

interface PriceStats {
  startDate: Date | null;
  endDate: Date | null;
  coverage: number;
  totalDays: number;
  missingDays: number;
  hourlyPattern: number[];
  hourlyStdDev?: number[];
  weekdayAverage: number;
  weekendAverage: number;
  seasonalAverages: {
    winter: number;
    spring: number;
    summer: number;
    fall: number;
  };
}

interface CompletionMethod {
  id: string;
  label: string;
  description: string;
}

interface PriceDataSectionProps {
  projectId?: string;
  prices: PriceData | null;
  priceStats: PriceStats;
  isAligned: boolean;
  selectedMethod: string;
  appliedMethod: string | null;
  showOriginal: boolean;
  completionMethods: CompletionMethod[];
  onMethodChange: (method: string) => void;
  onComplete: (shiftMonths?: number, duplicateToYear?: boolean, normalizeToYear?: boolean) => void;
  onToggle: () => void;
  onRevert: () => void;
}

export const PriceDataSection: React.FC<PriceDataSectionProps> = ({
  projectId,
  prices,
  priceStats,
  isAligned,
  selectedMethod,
  appliedMethod,
  showOriginal,
  completionMethods,
  onMethodChange,
  onComplete,
  onToggle,
  onRevert
}) => {
  const { t } = useTranslation();

  // Transformation state
  const [shiftMonths, setShiftMonths] = React.useState<number>(0);
  const [duplicateToYear, setDuplicateToYear] = React.useState<boolean>(false);
  const [normalizeToYear, setNormalizeToYear] = React.useState<boolean>(false);

  // Fetch dynamic pattern metrics
  const { data: metricsData, isLoading: metricsLoading } = usePatternMetrics(
    projectId,
    'price',
    isAligned
  );

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-swiss-charcoal flex items-center gap-2">
          <Calendar className="w-5 h-5 text-swiss-alpine" />
          {t('dataAlignment.prices.title')}
        </h4>
        {isAligned && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
      </div>

      {prices && prices.data && prices.data.length > 0 ? (
        <>
          <DataCoverageInfo {...priceStats} />
          
          <PatternAnalysis
            hourlyPattern={priceStats.hourlyPattern}
            hourlyStdDev={priceStats.hourlyStdDev}
            weekdayAverage={priceStats.weekdayAverage}
            weekendAverage={priceStats.weekendAverage}
            seasonalAverages={priceStats.seasonalAverages}
            unit="Price (USD/MWh)"
            color="#10B981"
            title="Price (USD/MWh)"
          />

          {/* Data Completion Controls */}
          <div className="border-t pt-4 space-y-4">
            <h5 className="text-sm font-semibold text-swiss-charcoal">{t('dataAlignment.prices.completeToFullYear')}</h5>

            <div className="space-y-3">
              {/* Method Selection */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-swiss-charcoal">
                  {t('dataAlignment.prices.completionMethod')}:
                </label>
                <select
                  value={selectedMethod}
                  onChange={(e) => onMethodChange(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                >
                  {completionMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <div className="relative group">
                  <Info className="w-4 h-4 text-gray-400 cursor-help" />
                  <div className="absolute z-10 w-96 p-4 bg-white border border-gray-200 rounded-lg shadow-lg
                                  opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                  transition-all duration-200 -left-48 top-6">
                    <h5 className="font-semibold text-sm mb-2">{t('dataAlignment.prices.metricsTitle')}</h5>
                    {metricsLoading ? (
                      <div className="text-xs text-gray-500">{t('dataAlignment.prices.loadingMetrics')}</div>
                    ) : !metricsData || !metricsData.success || !metricsData.metrics?.summary ? (
                      <div className="text-xs text-gray-500">
                        {!isAligned ? t('dataAlignment.prices.metricsNotAligned') : t('dataAlignment.prices.metricsUnavailable')}
                      </div>
                    ) : (
                      <div className="text-xs space-y-2 text-gray-600">
                        {/* Summary */}
                        <p className={`font-semibold ${
                          metricsData.metrics.summary.quality === 'excellent' ? 'text-green-600' :
                          metricsData.metrics.summary.quality === 'good' ? 'text-blue-600' :
                          metricsData.metrics.summary.quality === 'fair' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {metricsData.metrics.summary.quality_label}: {metricsData.metrics.summary.within_target}/{metricsData.metrics.summary.total_metrics} metrics within target ({metricsData.metrics.summary.pct_within_target}%)
                        </p>

                        {/* Metrics within target */}
                        {metricsData.metrics.summary.all_metrics.filter(m => m.within_target).length > 0 && (
                          <>
                            <p className="font-medium text-green-600 mt-2">{t('dataAlignment.prices.withinTarget')}</p>
                            <ul className="ml-4 list-disc space-y-1">
                              {metricsData.metrics.summary.all_metrics
                                .filter(m => m.within_target)
                                .slice(0, 5)
                                .map(m => (
                                  <li key={m.name}>{m.name}: {m.value.toFixed(2)}% ✅</li>
                                ))}
                            </ul>
                          </>
                        )}

                        {/* Metrics needing improvement */}
                        {metricsData.metrics.summary.all_metrics.filter(m => !m.within_target).length > 0 && (
                          <>
                            <p className="font-medium text-yellow-600 mt-2">{t('dataAlignment.prices.needsImprovement')}</p>
                            <ul className="ml-4 list-disc space-y-1">
                              {metricsData.metrics.summary.all_metrics
                                .filter(m => !m.within_target)
                                .slice(0, 5)
                                .map(m => (
                                  <li key={m.name}>{m.name}: {m.value.toFixed(2)}% ({t('dataAlignment.prices.targetLessThan')})</li>
                                ))}
                            </ul>
                          </>
                        )}

                        <p className="text-gray-500 mt-2 italic">{t('dataAlignment.prices.metricsNote')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-xs text-swiss-stone">
                {completionMethods.find(m => m.id === selectedMethod)?.description}
              </p>

              {/* Transformation Options */}
              <div className="border-t pt-3">
                <p className="text-xs text-swiss-stone font-medium mb-2">{t('dataAlignment.consumption.optionalTransform')}</p>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={normalizeToYear}
                      onChange={(e) => setNormalizeToYear(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-swiss-charcoal">{t('dataAlignment.consumption.normalizeTo2024')}</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={duplicateToYear}
                      onChange={(e) => setDuplicateToYear(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-swiss-charcoal">{t('dataAlignment.consumption.duplicateMonthsFirst')}</span>
                  </label>

                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-swiss-charcoal">
                      {t('dataAlignment.consumption.shiftFirst')}:
                    </label>
                    <select
                      value={shiftMonths}
                      onChange={(e) => setShiftMonths(parseInt(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                    >
                      <option value="0">{t('dataAlignment.consumption.noShift')}</option>
                      <option value="-12">-12 {t('dataAlignment.consumption.months')}</option>
                      <option value="-6">-6 {t('dataAlignment.consumption.months')}</option>
                      <option value="-3">-3 {t('dataAlignment.consumption.months')}</option>
                      <option value="-2">-2 {t('dataAlignment.consumption.months')}</option>
                      <option value="-1">-1 {t('dataAlignment.consumption.month')}</option>
                      <option value="1">+1 {t('dataAlignment.consumption.month')}</option>
                      <option value="2">+2 {t('dataAlignment.consumption.months')}</option>
                      <option value="3">+3 {t('dataAlignment.consumption.months')}</option>
                    </select>
                  </div>
                </div>

                {/* Transformation preview message */}
                {(normalizeToYear || duplicateToYear || shiftMonths !== 0) && (
                  <p className="text-xs text-blue-600 mt-2">
                    {normalizeToYear && "✓ Will normalize timestamps to 2024. "}
                    {shiftMonths !== 0 && `✓ Will shift by ${shiftMonths} months. `}
                    {duplicateToYear && "✓ Will duplicate to fill year."}
                  </p>
                )}
              </div>

              {/* Apply Button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => onComplete(shiftMonths, duplicateToYear, normalizeToYear)}
                  className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded hover:bg-brand-secondary transition-colors"
                >
                  {isAligned && appliedMethod !== selectedMethod ?
                    t('dataAlignment.prices.reCompleteWith', { method: completionMethods.find(m => m.id === selectedMethod)?.label }) :
                    t('dataAlignment.prices.completeToFullYear')}
                </button>
              </div>
            </div>
          </div>

          {/* Note when data was already complete */}
          {isAligned && priceStats.coverage === 100 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
              <p className="font-medium">{t('dataAlignment.prices.alreadyCompleteNote')}</p>
              <p className="text-xs mt-1">{t('dataAlignment.prices.alreadyCompleteMessage')}</p>
            </div>
          )}

          {/* Alignment Toggle - Moved above Alignment Validation */}
          <AlignmentToggle
            isAligned={isAligned}
            showOriginal={showOriginal}
            appliedMethod={appliedMethod}
            selectedMethod={selectedMethod}
            completionMethods={completionMethods}
            onToggle={onToggle}
            onRevert={onRevert}
          />

          {/* Alignment Comparison Table */}
          {isAligned && (
            <div className="border-t pt-4">
              <h5 className="text-sm font-semibold text-swiss-charcoal mb-3">{t('dataAlignment.prices.alignmentValidation')}</h5>
              <AlignmentComparisonTable
                projectId={projectId}
                dataType="price"
                isAligned={isAligned}
                title="Price Alignment Comparison"
              />
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-swiss-stone">
          <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>{t('dataAlignment.prices.noDataUploaded')}</p>
        </div>
      )}
    </Card>
  );
};